import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import EmptyState from "../components/EmptyState";
import {
  ListToolbar,
  type MenuItem,
  type SortOption,
} from "../components/ListToolbar";
import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { downloadCsv } from "../utils/downloadCsv";
import { A } from "../styles/appleClasses";
import { Button } from "../components/Button"; // ✅ tertiary button fix
import ListTableShell from "../components/lists/ListTableShell";
import { formatMoney } from "../utils/formatNumber";

// ---- Types ----
interface VendorLite {
  id: number;
  name: string;
  email?: string;
}
interface ProductBase {
  id: number;
  name: string;
  sku?: string;
  vendor?: VendorLite;
  stock_qty?: number;
  sell_price: number;
  cost_price?: number;
  is_service?: boolean;
}
interface InventoryItem extends ProductBase {
  avg_cost?: number; // weighted-average cost
  inventory_value?: number; // qty * avg_cost
}

const ROWS_PER_PAGE = 10;

// ---- Helpers (keep valuation logic identical) ----
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const toMoney = (n: number | null | undefined) => {
  if (typeof n !== "number") return "—";
  return formatMoney(n);
};

// Sum exactly what’s displayed per row (pre-rounded)
const displayedRowValue = (p: InventoryItem) => {
  const qty = p.stock_qty ?? 0;
  if (qty === 0) return 0;

  const wac =
    p.avg_cost != null && p.avg_cost > 0 ? p.avg_cost : (p.cost_price ?? 0);
  const raw =
    typeof p.inventory_value === "number" ? p.inventory_value : qty * wac;

  return round2(raw);
};

const unitCost = (p: InventoryItem) =>
  p.avg_cost != null && p.avg_cost > 0
    ? p.avg_cost
    : (p.cost_price ?? undefined);

function useRefetchOnFocus(refetch: () => void) {
  useEffect(() => {
    const onFocus = () => refetch();
    const onVis = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refetch]);
}

function ymdTodayLocal() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function csvEscape(v: unknown) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

type SortKey = "name" | "sku" | "stock" | "sell" | "cost" | "value";
type SortDir = "asc" | "desc";

type DisplayError = { local: "loadFailed" } | { raw: string } | null;

export default function ProductList() {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    loadFailed: t("directory.products.loadFailed"),
  })[error.local];

  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [serverTotal, setServerTotal] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DisplayError>(null);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);

  // TODO: replace this with your real role / permission check
  const [me, setMe] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const resp = await apiGet("/auth/me");
        if (!cancelled) setMe(resp);
      } catch {
        if (!cancelled) setMe(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const isProductAdmin = !!(me?.is_company_admin || me?.is_ricebook_admin);
  const isStaffUser = !!me && !isProductAdmin;

  const canSeeCost = isProductAdmin;
  const canOpenProductDetail = isProductAdmin;
  const canCreateProduct = isProductAdmin;

  const fetchProducts = useCallback(async () => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    try {
      const resp: any = await apiGet(
        `/products?include=valuation&_=${Date.now()}`,
      );
      const arr: any[] = Array.isArray(resp) ? resp : (resp?.items ?? []);

      const itemsNorm: InventoryItem[] = arr.map((p: any) => ({
        id: p.id,
        name: p.name,
        sku: p.sku,
        vendor: p.vendor,
        stock_qty: p.stock_qty ?? 0,
        sell_price: p.sell_price ?? 0,
        cost_price: p.cost_price ?? 0,
        is_service: !!p.is_service,
        avg_cost: p.avg_cost ?? 0,
        inventory_value:
          typeof p.inventory_value === "number" ? p.inventory_value : undefined,
      }));

      const total =
        !Array.isArray(resp) && typeof resp?.total_value === "number"
          ? round2(resp.total_value)
          : null;

      if (!cancelled) {
        setItems(itemsNorm);
        setServerTotal(total);
        setPage(1);
      }
    } catch {
      if (!cancelled) setError({ local: "loadFailed" });
    } finally {
      if (!cancelled) setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.search]);

  useRefetchOnFocus(fetchProducts);

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir, items.length]);

  useEffect(() => {
    if (!canSeeCost && (sortKey === "cost" || sortKey === "value")) {
      setSortKey("name");
      setSortDir("asc");
    }
  }, [canSeeCost, sortKey]);

  const clearFilters = () => {
    setSearch("");
    setSortKey("name");
    setSortDir("asc");
    setPage(1);
  };

  // ---- Filtering (same fields as your original) ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;

    return items.filter((p) => {
      const name = p.name?.toLowerCase() || "";
      const sku = p.sku?.toLowerCase() || "";
      const vendor = p.vendor?.name?.toLowerCase() || "";
      return (
        name.includes(q) ||
        sku.includes(q) ||
        vendor.includes(q) ||
        (p.stock_qty != null ? String(p.stock_qty).includes(q) : false) ||
        (p.sell_price != null ? String(p.sell_price).includes(q) : false) ||
        (canSeeCost && p.avg_cost != null
          ? String(p.avg_cost).includes(q)
          : false)
      );
    });
  }, [items, search, canSeeCost]);

  // ---- Sorting ----
  const sorted = useMemo(() => {
    const list = filtered.slice();
    const sign = sortDir === "asc" ? 1 : -1;

    const str = (s?: string) => s || "";
    const cmpStr = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }) * sign;

    const num = (n?: number | null) => (typeof n === "number" ? n : 0);

    list.sort((a, b) => {
      if (sortKey === "name") {
        const c = cmpStr(str(a.name), str(b.name));
        if (c !== 0) return c;
        return (a.id - b.id) * sign;
      }

      if (sortKey === "sku") {
        const c = cmpStr(str(a.sku), str(b.sku));
        if (c !== 0) return c;
        return cmpStr(str(a.name), str(b.name));
      }

      if (sortKey === "stock") {
        const c = (num(a.stock_qty) - num(b.stock_qty)) * sign;
        if (c !== 0) return c;
        return cmpStr(str(a.name), str(b.name));
      }

      if (sortKey === "sell") {
        const c = (num(a.sell_price) - num(b.sell_price)) * sign;
        if (c !== 0) return c;
        return cmpStr(str(a.name), str(b.name));
      }

      if (sortKey === "cost") {
        const c = (num(unitCost(a)) - num(unitCost(b))) * sign;
        if (c !== 0) return c;
        return cmpStr(str(a.name), str(b.name));
      }

      // sortKey === "value"
      const c = (displayedRowValue(a) - displayedRowValue(b)) * sign;
      if (c !== 0) return c;
      return cmpStr(str(a.name), str(b.name));
    });

    return list;
  }, [filtered, sortKey, sortDir]);

  // ---- Pagination (1-based) ----
  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const paged = sorted.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  // ---- Totals (same logic) ----
  const pageTotal = useMemo(
    () => round2(paged.reduce((sum, p) => sum + displayedRowValue(p), 0)),
    [paged],
  );

  const inventoryTotal = useMemo(() => {
    if (typeof serverTotal === "number") return serverTotal; // authoritative from server
    return round2(items.reduce((sum, p) => sum + displayedRowValue(p), 0));
  }, [serverTotal, items]);

  // ---- ListToolbar wiring ----
  const sortValue = `${sortKey}_${sortDir}`;
  const sortOptions: SortOption[] = [
    { label: t("directory.products.sortAsc"), value: "name_asc" },
    { label: t("directory.products.sortDesc"), value: "name_desc" },
    { label: t("directory.products.skuAsc"), value: "sku_asc" },
    { label: t("directory.products.skuDesc"), value: "sku_desc" },
    { label: t("directory.products.stockDesc"), value: "stock_desc" },
    { label: t("directory.products.stockAsc"), value: "stock_asc" },
    { label: t("directory.products.sellDesc"), value: "sell_desc" },
    { label: t("directory.products.sellAsc"), value: "sell_asc" },
    ...(canSeeCost
      ? [
          { label: t("directory.products.costDesc"), value: "cost_desc" as const },
          { label: t("directory.products.costAsc"), value: "cost_asc" as const },
          { label: t("directory.products.valueDesc"), value: "value_desc" as const },
          { label: t("directory.products.valueAsc"), value: "value_asc" as const },
        ]
      : []),
  ];

  const onToolbarSortChange = (v: string) => {
    const [k, d] = v.split("_") as [SortKey, SortDir];
    if (!canSeeCost && (k === "cost" || k === "value")) {
      setSortKey("name");
      setSortDir("asc");
      return;
    }
    setSortKey(k);
    setSortDir(d);
  };

  const handleHeaderSort = (k: SortKey) => {
    if (!canSeeCost && (k === "cost" || k === "value")) return;

    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      // default directions: strings asc, numbers desc
      setSortDir(k === "name" || k === "sku" ? "asc" : "desc");
    }
  };

  const headerArrow = (k: SortKey) =>
    sortKey === k ? (
      <span className="text-gray-400 ml-1">
        {sortDir === "asc" ? "↑" : "↓"}
      </span>
    ) : null;

  const exportCsv = () => {
    const header = canSeeCost
      ? [
          "Product ID",
          "Name",
          "SKU",
          "Vendor",
          "Stock",
          "Sell Price",
          "Cost",
          "Value",
        ]
      : ["Product ID", "Name", "SKU", "Vendor", "Stock", "Sell Price"];

    const rows = sorted.map((p) => {
      const qty = p.stock_qty ?? 0;
      const cost = unitCost(p);
      const value = qty ? displayedRowValue(p) : 0;

      return canSeeCost
        ? [
            p.id,
            p.name || "",
            p.sku || "",
            p.vendor?.name || "",
            qty,
            p.sell_price ?? 0,
            typeof cost === "number" ? cost : "",
            qty ? value : "",
          ]
        : [
            p.id,
            p.name || "",
            p.sku || "",
            p.vendor?.name || "",
            qty,
            p.sell_price ?? 0,
          ];
    });

    const csv =
      header.map(csvEscape).join(",") +
      "\n" +
      rows.map((r) => r.map(csvEscape).join(",")).join("\n");

    downloadCsv(`products_${ymdTodayLocal()}.csv`, csv);
  };

  const newItems: MenuItem[] = canCreateProduct
    ? [
        {
          label: t("directory.products.new"),
          onClick: () => navigate(companyRoute(company_id, "/products/new")),
        },
      ]
    : [];

  const moreItems: MenuItem[] = [{ label: t("directory.common.exportCsv"), onClick: exportCsv }];

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    `hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  // ---- UI ----
  if (loading) {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body}`}>
        <div className="w-full max-w-5xl mx-auto px-2 sm:px-6 pt-4 pb-10">
          <div className={`${A.card} p-6 text-center text-gray-600`}>
            {t("directory.products.loading")}
          </div>
        </div>
      </div>
    );
  }

  if (error) return <EmptyState heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} message={errorText(error) ?? ""} />;

  if (!items.length) {
    return (
      <EmptyState heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")}
        message={t("directory.products.empty")}
        showNewButton={canCreateProduct}
        newButtonText={t("directory.products.newWithPlus")}
        newButtonPath={companyRoute(company_id, "/products/new")}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className="w-full max-w-5xl mx-auto px-2 sm:px-6 pt-4 pb-10">
        {/* Header (sticky + spacing tokens: px-2 + mb-2) */}
        <div className="sticky top-0 z-30 bg-gray-50 border-b border-gray-100 px-2 pb-2 mb-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={iconBtn}
              aria-label={t("directory.common.back")}
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
            </button>

            <h2 className={`flex-1 text-center ${A.title}`}>{t("directory.products.title")}</h2>

            <button
              type="button"
              onClick={() => navigate(companyRoute(company_id, "/"))}
              className={iconBtn}
              aria-label={t("directory.common.close")}
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <ListToolbar moreLabel={t("directory.common.more")} sortLabel={t("directory.common.sort")}
          maxWidthClass="max-w-5xl"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("directory.products.search")}
          sortValue={sortValue}
          sortOptions={sortOptions}
          onSortChange={onToolbarSortChange}
          showDateRange={false}
          newItems={newItems}
          moreItems={moreItems}
          rightSummary={
            <div className="flex items-end gap-6 text-right leading-tight">
              <div>
                <div className={A.label}>{t("directory.products.summary")}</div>
                <div
                  className={`text-[13px] font-semibold text-gray-900 ${A.num}`}
                >
                  {totalFiltered}
                </div>
              </div>

              {canSeeCost && (
                <div>
                  <div className={A.label}>{t("directory.products.summaryProduct")}</div>
                  <div
                    className={`text-[13px] font-semibold text-gray-900 ${A.num}`}
                  >
                    {toMoney(inventoryTotal)}
                  </div>
                </div>
              )}
            </div>
          }
        />

        {/* Filter produced no results */}
        {items.length > 0 && totalFiltered === 0 ? (
          <div className="px-2">
            <div className={`${A.card} p-6 text-center text-gray-600`}>
              {t("directory.common.noResults")}
              <button
                type="button"
                onClick={clearFilters}
                className="ml-2 text-[13px] font-normal text-appleblue hover:text-appleblueD focus:outline-none focus:ring-2 focus:ring-appleblueD rounded"
              >
                {t("directory.common.clearFilters")}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="block md:hidden space-y-3 px-2">
              {paged.map((p) => {
                const qty = p.stock_qty ?? 0;
                const cost = unitCost(p);
                const value = qty ? displayedRowValue(p) : 0;
                const isService = !!p.is_service;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={[
                      A.card,
                      "w-full text-left p-4 transition",
                      canOpenProductDetail
                        ? "hover:bg-gray-50 active:ring-2 active:ring-appleblue/20"
                        : "",
                      "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                    ].join(" ")}
                    onClick={() => {
                      if (!canOpenProductDetail) return;
                      navigate(companyRoute(company_id, `/products/${p.id}`));
                    }}
                    aria-label={t("directory.products.view", { name: p.name })}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-700 truncate">
                          {p.name || "—"}
                        </div>
                        <div className={`${A.label} truncate`}>
                          {p.sku || "—"}
                        </div>
                        {p.vendor?.name ? (
                          <div className={`${A.label} text-gray-400 truncate`}>
                            {t("directory.products.vendorPrefix", { name: p.vendor.name })}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {canSeeCost ? (
                      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
                        <div className={A.label}>
                          {t("directory.products.stock")}{" "}
                          <span
                            className={`ml-1 text-[13px] text-gray-700 ${A.num} font-medium`}
                          >
                            {isService ? "—" : qty}
                          </span>
                        </div>

                        <div className={`${A.label} text-right`}>
                          {t("directory.products.value")}{" "}
                          <span
                            className={`ml-1 text-[13px] text-gray-700 ${A.num} font-medium`}
                          >
                            {isService ? "—" : qty ? toMoney(value) : "—"}
                          </span>
                        </div>

                        <div className={A.label}>
                          {t("directory.products.sell")}{" "}
                          <span
                            className={`ml-1 text-[13px] text-gray-700 ${A.num}`}
                          >
                            {toMoney(p.sell_price)}
                          </span>
                        </div>

                        <div className={`${A.label} text-right`}>
                          {t("directory.products.cost")}{" "}
                          <span
                            className={`ml-1 text-[13px] text-gray-700 ${A.num}`}
                          >
                            {isService
                              ? "—"
                              : typeof cost === "number"
                                ? toMoney(cost)
                                : "—"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 space-y-1">
                        <div className={A.label}>
                          {t("directory.products.stock")}{" "}
                          <span
                            className={`ml-1 text-[13px] text-gray-700 ${A.num} font-medium`}
                          >
                            {isService ? "—" : qty}
                          </span>
                        </div>

                        <div className={A.label}>
                          {t("directory.products.sell")}{" "}
                          <span
                            className={`ml-1 text-[13px] text-gray-700 ${A.num}`}
                          >
                            {toMoney(p.sell_price)}
                          </span>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block w-full max-w-5xl mx-auto">
              <ListTableShell>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th
                        className="py-2 px-4 text-left text-xs text-gray-500 select-none cursor-pointer"
                        title={t("directory.products.sortProduct")}
                        onClick={() => handleHeaderSort("name")}
                      >
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          {t("directory.products.product")} {headerArrow("name")}
                        </span>
                      </th>

                      {/* ✅ SKU column removed */}

                      <th
                        className={`py-2 px-2 text-right text-xs text-gray-500 select-none cursor-pointer w-20 ${A.num}`}
                        title={t("directory.products.sortStock")}
                        onClick={() => handleHeaderSort("stock")}
                      >
                        {t("directory.products.stock")} {headerArrow("stock")}
                      </th>

                      <th
                        className={`py-2 px-2 text-right text-xs text-gray-500 select-none cursor-pointer w-32 ${A.num}`}
                        title={t("directory.products.sortSell")}
                        onClick={() => handleHeaderSort("sell")}
                      >
                        {t("directory.products.sell")} {headerArrow("sell")}
                      </th>

                      {canSeeCost && (
                        <th
                          className={`py-2 px-2 text-right text-xs text-gray-500 select-none cursor-pointer w-32 ${A.num}`}
                          title={t("directory.products.sortCost")}
                          onClick={() => handleHeaderSort("cost")}
                        >
                          {t("directory.products.cost")} {headerArrow("cost")}
                        </th>
                      )}

                      {canSeeCost && (
                        <th
                          className={`py-2 pl-4 pr-6 text-right text-xs text-gray-500 select-none cursor-pointer w-36 ${A.num}`}
                          title={t("directory.products.sortValue")}
                          onClick={() => handleHeaderSort("value")}
                        >
                          {t("directory.products.value")} {headerArrow("value")}
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {paged.map((p) => {
                      const qty = p.stock_qty ?? 0;
                      const cost = unitCost(p);
                      const value = qty ? displayedRowValue(p) : 0;
                      const isService = !!p.is_service;
                      return (
                        <tr
                          key={p.id}
                          className={[
                            "border-b border-gray-100 transition",
                            canOpenProductDetail
                              ? "hover:bg-gray-50 cursor-pointer"
                              : "cursor-default",
                          ].join(" ")}
                          onClick={() => {
                            if (!canOpenProductDetail) return;
                            navigate(
                              companyRoute(company_id, `/products/${p.id}`),
                            );
                          }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && canOpenProductDetail) {
                              navigate(
                                companyRoute(company_id, `/products/${p.id}`),
                              );
                            }
                          }}
                          style={{ outline: "none" }}
                          aria-label={t("directory.products.view", { name: p.name })}
                        >
                          <td className="py-2 px-4 max-w-[24rem] truncate font-medium text-gray-700">
                            {p.name}
                          </td>

                          {/* ✅ SKU cell removed */}

                          <td className={`py-2 px-2 text-right ${A.num}`}>
                            {isService ? "—" : qty}
                          </td>
                          <td className={`py-2 px-2 text-right ${A.num}`}>
                            {toMoney(p.sell_price)}
                          </td>

                          {canSeeCost && (
                            <td className={`py-2 px-2 text-right ${A.num}`}>
                              {isService
                                ? "—"
                                : typeof cost === "number"
                                  ? toMoney(cost)
                                  : "—"}
                            </td>
                          )}

                          {canSeeCost && (
                            <td
                              className={`py-2 pl-4 pr-6 text-right ${A.num} font-medium`}
                            >
                              {isService ? "—" : qty ? toMoney(value) : "—"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ListTableShell>

              {/* Pagination */}
              <div className="mt-3 flex justify-between items-center text-[13px]">
                <span
                  className={`${A.label} text-gray-500 select-none ${A.num}`}
                >
                  {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
                </span>

                <div className="flex gap-2">
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    {t("directory.common.prev")}
                  </Button>
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    {t("directory.common.next")}
                  </Button>
                </div>
              </div>
            </div>

            {/* Pagination (mobile) */}
            <div className="md:hidden flex justify-between items-center mt-4 px-2">
              <span className={`${A.label} text-gray-500 select-none ${A.num}`}>
                {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
              </span>

              <div className="flex gap-3">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t("directory.common.prev")}
                </Button>
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
